import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import scrollIntoView from 'scroll-into-view-if-needed';

import { EventBusService } from '../../services/event-bus.service';
import { ReadingGroupsService } from '../../services/reading-groups.service';
import { ReadingGroup, ReadingGroupMember } from 'shared/models';
import { AnnotationsV2Service } from '../../services/annotations-v2.service';
import { DialogService, ToastService, TypedDialog } from 'shared/ui';
import { UserDetailsDialogComponent } from '../user-details-dialog/user-details-dialog.component';
import {
  APIError,
  ReadingGroupMembersAPIService,
  UsersAPIService,
} from 'shared/services';
import { OnlineReadingMemberService } from '../../services/online-reading-member.service';
import { NoteSendDialogComponent } from 'user';

@Component({
  selector: 'viewer-reading-group-members-dialog',
  templateUrl: './reading-group-members-dialog.component.html',
  styleUrl: './reading-group-members-dialog.component.scss',
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: {
    class: 'mobile-full-height',
  },
})
export class ReadingGroupMembersDialogComponent
  extends TypedDialog<{ bid: string; userId?: string }, void>
  implements OnInit, OnDestroy
{
  @ViewChild('listElem', { static: true })
  private _listElem!: ElementRef<HTMLDivElement>;

  public _group!: ReadingGroup;

  public _members?: (ReadingGroupMember & { isOnline?: boolean })[];
  public _isSelectionMode = false;

  public _selectedMemberId?: string;
  public _selectedMemberName?: string;
  public _totalMemberCount?: number;
  public _onlineMemberCount?: number;

  private _isDialogOpened = false;

  private _unsubscriber = new Subject<void>();

  constructor(
    private _eventBusService: EventBusService,
    private _readingGroupsService: ReadingGroupsService,
    private _annotationsService: AnnotationsV2Service,
    private _toastService: ToastService,
    private _dialogService: DialogService,
    private _readingGroupMembersAPIService: ReadingGroupMembersAPIService,
    private _onlineReadingGroupMemberService: OnlineReadingMemberService,
    private _usersAPIService: UsersAPIService
  ) {
    super();
  }

  ngOnInit(): void {
    this._group = this._readingGroupsService.currentGroup!;
    this._selectedMemberId = this._annotationsService.getState().memberId;
    this._isSelectionMode = this._selectedMemberId !== undefined;

    // AnnotationsV2Service의 state$를 구독하여 memberId가 제거되면
    // isSelectionMode를 false로 설정
    this._annotationsService.state$
      .pipe(
        map((state) => state.memberId),
        takeUntil(this._unsubscriber)
      )
      .subscribe((memberId) => {
        if (!memberId && this._isSelectionMode) {
          this._isSelectionMode = false;
        }
        this._selectedMemberId = memberId;
      });

    this._loadMembers(this._group.id);

    if (this._selectedMemberId) {
      this._dialogRef.afterOpened().subscribe(() => {
        this._isDialogOpened = true;
        this._scrollToSelectedMember();
      });
    }

    this._onlineReadingGroupMemberService.onlineStatusChange$
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((data) => {
        const member = this._members?.find((m) => m.id === data.userId);

        if (!member) {
          return;
        }

        member.isOnline = data.isOnline;
        this._onlineMemberCount = this._members?.filter(
          (member) => member.isOnline || !!member.is_me
        ).length;
      });

    // user-details-dialog에서 부모 다이얼로그 닫기 요청을 구독
    this._eventBusService
      .on('UserDetailsDialogComponent:closeParentDialog')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        this._close();
      });
  }

  ngOnDestroy(): void {
    this._unsubscriber.next();
    this._unsubscriber.complete();
  }

  onCloseButtonClick(): void {
    this._close();
  }

  _onClickAvatar(member: ReadingGroupMember): void {
    this._dialogService
      .open(UserDetailsDialogComponent, {
        data: {
          bid: this._data.bid,
          userId: member.id,
          isUserBlocked: !!member.is_blocked,
          groupId: this._group.id,
          isReOpen: true,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result) {
          return;
        }

        member.is_blocked = result.userBlocked ? 1 : 0;
        member.is_follow = result.userFollowed ? 1 : 0;

        if (result.seeDetailsButtonClicked) {
          this._dialogRef.close();
        }
      });
  }

  private _close(): void {
    this._dialogRef.close();
  }

  private _onLoadMembers(): void {
    this._members?.forEach((member) => {
      member.isOnline = this._onlineReadingGroupMemberService.isOnline(
        member.id
      );
    });

    this._onlineMemberCount = this._members?.filter(
      (member) => member.isOnline || !!member.is_me
    ).length;

    if (this._selectedMemberId) {
      this._selectedMemberName = this._members?.find(
        (member) => member.id === this._selectedMemberId
      )?.nickname;

      setTimeout(() => {
        this._scrollToSelectedMember();
      });
    }

    if (this._data.userId) {
      const member = this._members?.find(
        (member) => member.id === this._data.userId
      );

      if (member) {
        this._onClickAvatar(member);
      }
    }
  }

  private _loadMembers(groupId: number): void {
    if (this._members) {
      this._onLoadMembers();
      return;
    }

    this._readingGroupMembersAPIService
      .getJoined(groupId)
      .subscribe((response) => {
        this._members = response;
        this._totalMemberCount = response.length;

        this._onLoadMembers();
      });
  }

  private _scrollToSelectedMember(): void {
    if (!this._members || !this._isDialogOpened) {
      return;
    }

    const selectedMember = this._listElem.nativeElement.querySelector(
      '.list-item.is-selected'
    );

    selectedMember &&
      scrollIntoView(selectedMember, {
        block: 'center',
        scrollMode: 'if-needed',
        boundary: this._listElem.nativeElement,
      });
  }

  _followMember(member: ReadingGroupMember): void {
    const request$ = this._usersAPIService.follow(member.id);

    request$.subscribe({
      next: () => {
        member.is_follow = 1;
      },
      error: (error: APIError) => {
        this._toastService.openWarning(error.message);
      },
    });
  }

  _unfollowMember(member: ReadingGroupMember): void {
    const request$ = this._usersAPIService.unfollow(member.id);

    request$.subscribe({
      next: () => {
        member.is_follow = 0;
      },
      error: (error: APIError) => {
        this._toastService.openWarning(error.message);
      },
    });
  }

  _sendMessageToMember(member: ReadingGroupMember): void {
    this._dialogService.open(NoteSendDialogComponent, {
      data: {
        userId: member.id,
        nickname: member.nickname,
      },
    });
  }
}
